/**
 * The `rel` value for an anchor this app BUILDS from a user-authored URL field.
 *
 * <b>Not a second policy — the client-side counterpart of the backend's `UgcLinkRel`.</b> That class
 * stamps `rel` onto anchors that are already inside stored HTML (discussion bodies, thoughts). Some
 * user-authored links are not HTML at all: a group resource is a bare URL column that React turns into
 * an `<a href>`, so there is no anchor for a backend read-path stamp to attach to and the decision has
 * to be made where the anchor is created.
 *
 * <b>Why a host-dependent decision is allowed here.</b> The TipTap-renderer incident of 2026-07-31
 * established that "a renderer that cannot know the site host must not make a host-dependent decision"
 * — that renderer runs in a Lambda with no notion of which site it renders for, and its blanket
 * `nofollow` default silently nofollowed our own internal links for months. The operative half of that
 * rule is the part in italics: a browser running on seavien.com *does* know the host exactly, via
 * `window.location`. The same reasoning is why `linkifyMessageBody.tsx` has always set these tokens
 * client-side.
 *
 * Policy, token-for-token identical to `UgcLinkRel.POLICY_TOKENS`:
 * - off-site `http(s)` URL  →  `noopener noreferrer ugc nofollow`
 * - same-host, relative, fragment, or a non-`http(s)` scheme (`mailto:`, `tel:`)  →  `noopener noreferrer`
 *
 * Internal links stay followed on purpose: nofollowing our own navigation discards internal link
 * equity, which is the opposite of what the policy is for.
 *
 * Design: `designs/01-now/2026-07-29-ugc-outbound-link-rel-policy-design.md`.
 * Backend twin: `MainService/.../service/seo/UgcLinkRel.java` — change both together.
 */

/** Safety and privacy tokens. Carried by every user-content anchor regardless of host. */
export const BASE_ANCHOR_REL = "noopener noreferrer";

/** Added on top of {@link BASE_ANCHOR_REL} for off-site targets only. */
export const UGC_ANCHOR_REL = `${BASE_ANCHOR_REL} ugc nofollow`;

/**
 * Returns `true` when `href` is an absolute `http(s)` URL whose host differs from the page's own.
 *
 * Anything that is not an absolute `http(s)` URL — a relative path, a fragment, `mailto:`, `tel:`, or
 * an unparseable string — is treated as NOT off-site, so the safe default is "do not nofollow". That
 * direction is deliberate: over-stamping quietly nofollows our own internal linking, which is the
 * failure mode the policy's own Playwright assertion exists to catch.
 */
export function isOffSiteHref(href: string | null | undefined): boolean {
    const trimmed = (href ?? "").trim();
    if (!trimmed) {
        return false;
    }
    try {
        const parsed = new URL(trimmed, window.location.href);
        if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
            return false;
        }
        return parsed.host !== window.location.host;
    } catch {
        return false;
    }
}

/**
 * The `rel` to put on an anchor built from a user-authored URL.
 */
export function ugcAnchorRel(href: string | null | undefined): string {
    return isOffSiteHref(href) ? UGC_ANCHOR_REL : BASE_ANCHOR_REL;
}
